/**
 * CR-6449: supplier-view layout for the Sales Orders → ASN filter panel.
 *
 * Renders exactly the filter set from the supplied design, grouped into its
 * four sections — Date Range · Company · Supplier · Order Details — using the
 * same section shell (`FilterSection` + divider) and the same control density
 * (`deliveryFilterStyles`) as the Deliveries tab, which is the accepted
 * reference for every supplier-view filter rework. Filters the design omits
 * (Division, Company Country, Supplier Code, Strategic, Commodity Group/Type,
 * Buyer ID) stay available on the customer view only.
 *
 * Every control binds to the same filter key and the same `onFilterChange`
 * handler as before, so the cascade logic in `useASNFilterCascades` and the
 * query-param mapping in `ASNTab` are untouched. The one control-type change
 * is deliberate: Supplier Site is a searchable select (as on SO Headers and
 * Deliveries) so the Supplier Country / Group cascades visibly narrow it.
 */
import { Calendar, Building2, Truck, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection } from '../../../filters/FilterSection';
import {
  inputCls, labelCls, dateWrap, dateInputCls, gridCls,
} from '../delivery/deliveryFilterStyles';
import {
  ASN_STATUS_OPTIONS,
  ASN_COURIER_OPTIONS,
  ASN_COURIER_STATUS_OPTIONS,
  ASN_QUICK_SELECT_CHIPS,
  type ASNFilterOption,
} from './asnFilterOptions';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

export interface ASNFilterSupplierLayoutProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  /** Currently selected quick date range chip value ('' when none). */
  quickSelectValue: string;
  onQuickSelectChange: (value: string) => void;
  siteOptions: ASNFilterOption[];
  warehouseOptions: ASNFilterOption[];
  supplierCountryOptions: ASNFilterOption[];
  supplierGroupOptions: ASNFilterOption[];
  supplierSiteOptions: ASNFilterOption[];
  onFilterChange: (key: string, value: string) => void;
  /** Enter in a text field applies the filters, as it always has. */
  onApply?: () => void;
  /** CR-6160: courier delivery status is only offered with the tracking module. */
  trackingEnabled?: boolean;
  /** CR-6441 / CR-6160: Courier Delivery Status choices as served by the backend. */
  courierStatusOptions?: ASNFilterOption[];
}

export function ASNFilterSupplierLayout({
  filters,
  textInputs,
  quickSelectValue,
  onQuickSelectChange,
  siteOptions,
  warehouseOptions,
  supplierCountryOptions,
  supplierGroupOptions,
  supplierSiteOptions,
  onFilterChange,
  onApply,
  trackingEnabled = false,
  courierStatusOptions = ASN_COURIER_STATUS_OPTIONS,
}: ASNFilterSupplierLayoutProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const dateField = (label: string, key: 'dateFrom' | 'dateTo') => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className={dateWrap}>
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="date"
          value={filters[key] || ''}
          onChange={(e) => onFilterChange(key, e.target.value)}
          className={dateInputCls}
        />
      </div>
    </div>
  );

  const textField = (label: string, key: string, placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={textInputs[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
        className={inputCls}
      />
    </div>
  );

  const selectField = (label: string, key: string, options: ASNFilterOption[]) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  const searchableField = (label: string, key: string, options: ASNFilterOption[], placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <SearchableSelect
        options={options}
        value={filters[key] || ''}
        onChange={(val) => onFilterChange(key, val)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date Range — quick select chips on the left, the expected-delivery
          date pair on the right: the same arrangement as the customer ASN
          panel (client review, Sep 2026). */}
      <FilterSection title="Date Range" icon={Calendar}>
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap gap-2 max-w-2xl">
            {ASN_QUICK_SELECT_CHIPS.map(({ value, label }) => {
              const isActive = quickSelectValue === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => onQuickSelectChange(value)}
                  className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                    isActive
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="w-44">{dateField('Expected Delivery From', 'dateFrom')}</div>
            <div className="w-44">{dateField('Expected Delivery To', 'dateTo')}</div>
          </div>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company — Site narrows Warehouse via the cascade hook */}
      <FilterSection title="Company" icon={Building2}>
        <div className={gridCls}>
          {searchableField('Company Site', 'site', siteOptions, 'All Company Sites')}
          {fv.warehouse && (
          <div>
            <label className={labelCls}>Warehouse</label>
            <select
              value={filters['warehouse'] || ''}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={inputCls}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — Country and Group narrow Supplier Site via the cascade hook */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          {searchableField('Supplier Country', 'supplierCountry', supplierCountryOptions, 'All Supplier Countries')}
          {searchableField('Supplier Group', 'supplierGroup', supplierGroupOptions, 'All Supplier Groups')}
          {searchableField('Supplier Site', 'supplier', supplierSiteOptions, 'All Supplier Sites')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Order Details */}
      <FilterSection title="Order Details" icon={FileText}>
        <div className={gridCls}>
          {textField('ASN Number', 'asnNumber', 'ASN Number...')}
          {selectField('ASN Status', 'status', ASN_STATUS_OPTIONS)}
          {textField('PO Number', 'poNumber', 'PO Number...')}
          {textField('PO Line Number', 'poLineNumber', 'PO Line Number...')}
          {textField('Part Number', 'partNumber', 'Part Number...')}
          {textField('Description', 'description', 'Description...')}
          {selectField('Courier', 'courier', ASN_COURIER_OPTIONS)}
          {textField('Tracking Number', 'trackingNumber', 'Tracking Number...')}
          {/* CR-6160: only offered when the logistics tracking module is enabled */}
          {trackingEnabled && selectField('Courier Delivery Status', 'courierDeliveryStatus', courierStatusOptions)}
        </div>
      </FilterSection>
    </div>
  );
}
